import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getCategoryIcon } from '../Layouts/Header/CategoryNav';
import { useLang, catName } from '../../utils/i18n';

// Normalizes any category name to sentence case (first letter capital,
// everything else lowercase) — regardless of how it was originally typed
// or migrated in ("DIWALI PRODUCTS", "Non Alcoholic Beer", etc.).
const toSentenceCase = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// A single category row. If it has children, hovering opens a submenu
// showing those children.
//
// IMPORTANT: the submenu is rendered with createPortal() straight into
// document.body, positioned with fixed pixel coordinates (measured from
// this row's position on screen), instead of being a normal CSS
// "position: absolute" child sitting inside the parent submenu's DOM tree.
//
// Why: the parent submenu needs "overflow-y-auto" + "max-h-[70vh]" so very
// long category lists don't run off the bottom of the screen. But in CSS,
// setting overflow-y to anything other than "visible" forces overflow-x to
// clip too - so that box becomes a clipping window for everything inside
// it, including further submenus positioned outside its edge. That's why
// level-2+ submenus were invisible (present in the DOM, but visually
// clipped) even though level-1 worked fine.
//
// Because a portaled element is NOT a DOM descendant of its parent submenu
// anymore, it can't be clipped by the parent's overflow-y-auto - at any
// nesting depth. This is a structural fix, not another positioning tweak.
const CategoryRow = ({ item, topLevel = false }) => {
    const { lang } = useLang();
    const [open, setOpen] = useState(false);
    const rowRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, bottom: 0, left: 0, openUpward: false });

    const hasChildren = item.children && item.children.length > 0;

    const handleEnter = () => {
        if (rowRef.current) {
            const rect = rowRef.current.getBoundingClientRect();
            // Rough estimate of how tall the submenu will be, so we can
            // decide whether it needs to flip upward to stay on screen.
            const estimatedHeight = Math.min((item.children?.length || 0) * 41 + 8, window.innerHeight * 0.7);
            const spaceBelow = window.innerHeight - rect.top;
            const openUpward = spaceBelow < estimatedHeight && rect.top > spaceBelow;
            setPos({ top: rect.top, bottom: rect.bottom, left: rect.right, openUpward });
        }
        setOpen(true);
    };

    const handleLeave = () => setOpen(false);

    return (
        <div ref={rowRef} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
            <Link
                to={`/products?category=${item.slug}`}
                className={
                    topLevel
                        ? 'flex items-center justify-between px-4 py-3 text-[15px] leading-relaxed text-gray-700 hover:bg-gray-50 hover:text-primary-blue'
                        : 'flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:text-primary-blue hover:bg-gray-50'
                }
            >
                <span className="flex items-center gap-3">
                    {topLevel && <span className="text-gray-400">{getCategoryIcon(item.name, '20px')}</span>}
                    {lang === 'zh' ? catName(item, lang) : toSentenceCase(item.name)}
                </span>
                {hasChildren && <ChevronRightIcon sx={{ fontSize: '16px' }} className="text-gray-400" />}
            </Link>

            {hasChildren && open && createPortal(
                <div
                    className="fixed bg-white shadow-2xl border w-64 max-h-[70vh] overflow-y-auto z-50"
                    style={{
                        left: pos.left,
                        top: pos.openUpward ? 'auto' : pos.top,
                        bottom: pos.openUpward ? window.innerHeight - pos.bottom : 'auto',
                    }}
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={handleLeave}
                >
                    {item.children.map((child) => (
                        <CategoryRow key={child._id} item={child} />
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

const CategorySidebar = () => {
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                setCategories(data.categories.slice(0, 10));
            } catch (error) {
                console.error('Failed to load categories', error);
            }
        };
        fetchCategories();
    }, []);

    return (
        <div className="hidden lg:block w-72 h-full flex-shrink-0 bg-white border-0 rounded-b-lg shadow-sm overflow-visible">
            <div className="bg-primary-green text-white font-semibold text-sm uppercase tracking-wide px-4 py-3 rounded-t-lg">
                Categories
            </div>
            {categories.map((cat) => (
                <CategoryRow key={cat._id} item={cat} topLevel />
            ))}
        </div>
    );
};

export default CategorySidebar;